/**
 * Route-aware error boundary (issue #2359).
 *
 * Wraps the routed page tree in a `Sentry.ErrorBoundary` whose fallback RESETS
 * on navigation. This is the safety net that guarantees a transient error caught
 * on one screen can never linger across a route change: the moment the location
 * pathname changes while the fallback is showing, the boundary is reset and the
 * destination screen renders.
 *
 * It sits INSIDE `<BrowserRouter>` (so the fallback can read `useLocation`) but
 * OUTSIDE the page `<Routes>`, wrapping only the routed content -- NOT the app
 * providers -- so a reset never remounts `AuthProvider`/branding/etc.
 *
 * The visual fallback + the refresh-window transient recovery both live in
 * `ErrorFallback`; this component only adds the navigation-driven reset. The
 * root `Sentry.ErrorBoundary` in `main.tsx` remains the catch-all for anything
 * thrown before the router mounts.
 */
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { useLocation } from "react-router-dom";
import ErrorFallback from "./ErrorFallback";

/**
 * The fallback rendered by the route boundary. Because it is rendered inside the
 * router it can observe the location and reset the boundary when the user
 * navigates away from the errored route -- so a caught transient never persists
 * across a navigation. Delegates the actual UI + refresh-window recovery to
 * `ErrorFallback`.
 */
function RouteErrorFallback({
  error,
  resetError,
}: {
  error: unknown;
  resetError: () => void;
}) {
  const location = useLocation();
  const errorPath = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname !== errorPath.current) {
      // Navigated to a different route while the fallback was showing -> clear
      // the boundary so the new screen renders (never a lingering crash card).
      resetError();
    }
  }, [location.pathname, resetError]);

  return <ErrorFallback error={error} resetError={resetError} />;
}

export default function RouteErrorBoundary({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <RouteErrorFallback error={error} resetError={resetError} />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
